// components/common/PharmacyFilters.jsx
import React from 'react';
import { X,ChevronUp } from 'lucide-react';

const PharmacyFilters = ({
  filters,
  onFilterChange,
  showAdvancedFilters,
  onToggleAdvancedFilters,
  onClearFilters
}) => {
  const hasActiveFilters = () => {
    return (
      filters.deliveryService ||
      filters.open24Hours ||
      filters.radius !== 25 ||
      filters.minRating ||
      filters.maxDeliveryFee ||
      filters.hasParking ||
      filters.wheelchairAccessible ||
      filters.acceptsInsurance
    );
  };

  return (
    <div className={`bg-white border-b border-gray-200 transition-all duration-300 ${
      showAdvancedFilters ? 'max-h-screen' : 'max-h-0 overflow-hidden'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            Filter Pharmacies
            {hasActiveFilters() && (
              <span className="bg-primary-100 text-primary-700 text-xs px-2 py-1 rounded-full">
                Active
              </span>
            )}
          </h3>
          <div className="flex items-center gap-2">
            {hasActiveFilters() && (
              <button
                onClick={onClearFilters}
                className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
              >
                <X size={16} />
                Clear All
              </button>
            )}
            <button
              onClick={onToggleAdvancedFilters}
              className="text-gray-500 hover:text-gray-700"
              aria-label="Close filters"
            >
              <ChevronUp size={20} />
            </button>
          </div>
        </div>

        {/* Filter Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          
          {/* Distance/Radius Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search Radius: {filters.radius}km
            </label>
            <input
              type="range"
              min="5"
              max="100"
              step="5"
              value={filters.radius}
              onChange={(e) => onFilterChange('radius', parseInt(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-600"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>5km</span>
              <span>100km</span>
            </div>
          </div>

          {/* Rating Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Minimum Rating
            </label>
            <select
              value={filters.minRating}
              onChange={(e) => onFilterChange('minRating', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Any Rating</option>
              <option value="4.5">4.5+ Stars</option>
              <option value="4.0">4.0+ Stars</option>
              <option value="3.5">3.5+ Stars</option>
              <option value="3.0">3.0+ Stars</option>
            </select>
          </div>

          {/* Max Delivery Fee */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Max Delivery Fee (KES)
            </label>
            <input
              type="number"
              placeholder="No limit"
              value={filters.maxDeliveryFee}
              onChange={(e) => onFilterChange('maxDeliveryFee', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              min="0"
              step="50"
            />
          </div>

          {/* Quick Filters */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Quick Filters
            </label>
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.deliveryService}
                  onChange={(e) => onFilterChange('deliveryService', e.target.checked)}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">Delivery Service</span>
              </label>
              
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.open24Hours}
                  onChange={(e) => onFilterChange('open24Hours', e.target.checked)}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">Open 24 Hours</span>
              </label>
            </div>
          </div>
        </div>

        {/* Additional Filters */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h4 className="text-sm font-medium text-gray-900 mb-4">Additional Filters</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={filters.hasParking}
                onChange={(e) => onFilterChange('hasParking', e.target.checked)}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">Parking Available</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={filters.wheelchairAccessible}
                onChange={(e) => onFilterChange('wheelchairAccessible', e.target.checked)}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">Wheelchair Accessible</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={filters.acceptsInsurance}
                onChange={(e) => onFilterChange('acceptsInsurance', e.target.checked)}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">Accepts Insurance</span>
            </label>
          </div>
        </div>

        {/* Active Filters Summary */}
        {hasActiveFilters() && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">
                {Object.values(filters).filter(v => v && v !== 25).length} filter(s) active
              </span>
              <button
                onClick={onClearFilters}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                Clear All Filters
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PharmacyFilters;